import { createFocusTrap } from './focus-trap';

const afterAnimations = (element: Element, callback: () => void, subtree = true) =>
    Promise.allSettled(element.getAnimations({ subtree }).map(animation => animation.finished)).then(callback);

export const header = () => {
    const headerElem = document.querySelector('.header');
    const secondLevelElem = headerElem?.querySelector<HTMLElement>('.header__second-level');
    const secondLevelNavContainerElem = secondLevelElem?.querySelector('.header__second-level-nav');
    const firstLevelNav = headerElem?.querySelector('.header__nav');
    let desktopMenuIsOpen = false;
    let mobileMenuIsOpen = false;
    const mobileViewport = window.matchMedia('(max-width: 1024px)');

    const isDesktop = () => {
        if (!secondLevelElem) return;
        const menuControls = () => Array.from(secondLevelElem.querySelectorAll<HTMLElement>('a[href], button:not(:disabled)'))
            .filter(element => element.getClientRects().length);

        const openSecondLevel = (secondLevelNavElem, toggleButton, shouldFocus: boolean = false) => {
            secondLevelNavContainerElem!.innerHTML = secondLevelNavElem.outerHTML;
            secondLevelElem!.inert = false;
            (secondLevelElem as HTMLElement).style.display = 'grid';
            const reflow = (secondLevelElem as HTMLElement).offsetHeight;
            secondLevelElem?.classList.add('header__second-level--showing');
            headerElem?.querySelectorAll('.header__nav-ul > li')?.forEach(li => {
                li.classList.remove('header__nav-item--active');
                li.querySelector('.header__nav-toggle')?.setAttribute('aria-expanded', 'false');
            });
            
            secondLevelElem?.setAttribute('data-active-toggle', toggleButton.id || 'toggle-button');
            
            toggleButton.setAttribute('aria-expanded', 'true');

            desktopMenuIsOpen = true;
            if (shouldFocus) menuControls()[0]?.focus();
        }
        
        const closeSecondLevel = (toggleButton: HTMLElement | null = null) => {
            if (!secondLevelElem)
                return;

            desktopMenuIsOpen = false;
            secondLevelElem?.classList.remove('header__second-level--showing');
            
            headerElem?.querySelectorAll('.header__nav-ul > li')?.forEach(li => {
                li.classList.remove('header__nav-item--active');
                const button = li.querySelector('button');
                if (button) {
                    button.setAttribute('aria-expanded', 'false');
                }
            });
            
            if (secondLevelElem.contains(document.activeElement)) {
                toggleButton ??= document.getElementById(secondLevelElem.getAttribute('data-active-toggle') || '');
            }
            if (toggleButton && 'focus' in toggleButton) {
                toggleButton.focus({ preventScroll: true });
            }
            secondLevelElem.inert = true;
            
            afterAnimations(secondLevelElem, () => {
                if (!desktopMenuIsOpen) secondLevelElem.style.display = 'none';
            });
        }
        
        //listener for opening second level
        headerElem?.querySelectorAll('.header__nav-ul > li')?.forEach(li => {
            const secondLevelNavElem = li.querySelector('.header__nav-item--second-level');
        
            if (secondLevelNavElem) {
                li.addEventListener('mouseover', () => {
                    if (window.innerWidth > 1024) {
                        openSecondLevel(secondLevelNavElem, li.querySelector('button'));
                        li.classList.add('header__nav-item--active');
                    }
                })

                li.querySelector('button')?.addEventListener('click', () => {
                    if (mobileViewport.matches) return;
                    if (li.classList.contains('header__nav-item--active')) {
                        closeSecondLevel(li.querySelector('button'));
                    } else { 
                        openSecondLevel(secondLevelNavElem, li.querySelector('button'));
                        li.classList.add('header__nav-item--active');
                    }
                })
            } else {
                li.addEventListener('mouseover', () => {
                    if (secondLevelElem?.classList.contains('header__second-level--showing')) {
                        closeSecondLevel();
                    }
                })
            }
        })
        
        //listeners for closing second level
        secondLevelElem?.addEventListener('mouseout', (e) => {
            if (!secondLevelElem.contains(document.activeElement) && !((e as MouseEvent).relatedTarget as HTMLElement)?.closest('.header__second-level')) {
                closeSecondLevel();
            }
        })
        secondLevelElem?.querySelector('.header__close-button')?.addEventListener('click', () => closeSecondLevel())
        headerElem?.querySelector('.header__desktop-dead-space')?.addEventListener('mouseover', () => closeSecondLevel())
        headerElem?.querySelector('.header__contact')?.addEventListener('mouseover', () => closeSecondLevel())

        // Enhanced keyboard navigation for toggle buttons
        headerElem?.querySelectorAll('.header__nav-toggle').forEach(button => {
            // Ensure each toggle button has a unique ID for better accessibility
            if (!button.id) {
                button.id = `nav-toggle-${Math.random().toString(36).substring(2, 9)}`;
            }
            
            button.addEventListener('keydown', (event: Event) => {
                if (mobileViewport.matches) return;
                const keyboardEvent = event as KeyboardEvent;
                
                // Tab key navigation when menu is open
                if (keyboardEvent.key === 'Tab' && !keyboardEvent.shiftKey && button.getAttribute('aria-expanded') === 'true') {
                    event.preventDefault();
                    
                    menuControls()[0]?.focus();
                }
                
                // Enter/Space to toggle menu
                if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                    event.preventDefault();
                    const parentLi = (button as HTMLElement).closest('.header__nav-item');
                    const secondLevelNavElem = parentLi?.querySelector('.header__nav-item--second-level');
                    
                    if (button.getAttribute('aria-expanded') === 'true') {
                        closeSecondLevel(button as HTMLElement);
                    } else {
                        openSecondLevel(secondLevelNavElem, button, true);
                        parentLi?.classList.add('header__nav-item--active');
                    }
                }
            });
        });

        secondLevelElem.addEventListener('keydown', event => {
            if (event.key !== 'Tab') return;
            const controls = menuControls();
            if (document.activeElement !== (event.shiftKey ? controls[0] : controls[controls.length - 1])) return;
            if (event.shiftKey) event.preventDefault();
            closeSecondLevel();
        });

        document.addEventListener('focusin', event => {
            if (!desktopMenuIsOpen || secondLevelElem.contains(event.target as Node)) return;
            const toggle = document.getElementById(secondLevelElem.getAttribute('data-active-toggle') || '');
            if (event.target !== toggle) closeSecondLevel();
        });

        // Handle escape key to close menu
        document.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Escape' && desktopMenuIsOpen) {
                const activeButton = headerElem?.querySelector('.header__nav-item--active button');
                closeSecondLevel(activeButton as HTMLElement);
            }
        });

        return () => {
            if (!desktopMenuIsOpen) return;
            const hadFocus = secondLevelElem?.contains(document.activeElement);
            closeSecondLevel();
            if (hadFocus && mobileViewport.matches) {
                headerElem?.querySelector<HTMLElement>('.header__mobile-controls')?.focus();
            }
        };
    }

    const isMobile = () => {
        const headerNavElem:HTMLElement | null | undefined = headerElem?.querySelector('.header__nav');
        const mobileMetaContainerElem:HTMLElement | null | undefined = headerElem?.querySelector('.header__meta-container--mobile');
        const mobileControlsElem = headerElem?.querySelector<HTMLElement>('.header__mobile-controls');
        const mobileMenuElem = headerElem?.querySelector<HTMLElement>('.header__first-level');

        if (!headerNavElem || !mobileMetaContainerElem || !mobileControlsElem || !mobileMenuElem)
            return;

        const trap = createFocusTrap(mobileMenuElem, '.skip-to-content, main, footer');

        mobileMenuElem.id = 'header__mobile-menu';
        mobileControlsElem.setAttribute('aria-controls', mobileMenuElem.id);
        mobileControlsElem.setAttribute('aria-haspopup', 'dialog');
        mobileControlsElem.setAttribute('aria-expanded', 'false');

        const contact = headerElem?.querySelector('.header__contact');
        const orderHeader = () => {
            headerNavElem.querySelectorAll<HTMLButtonElement>('.header__nav-toggle').forEach(button => {
                const submenu = button.nextElementSibling as HTMLElement;
                button.setAttribute('aria-controls', mobileViewport.matches ? submenu.id : 'header__second-level');
                button.setAttribute('aria-expanded', String(mobileViewport.matches && submenu.classList.contains('header__nav-item--second-level--showing')));
            });
            if (!contact) return;
            const focused = document.activeElement as HTMLElement;
            if (mobileViewport.matches) mobileControlsElem.before(contact);
            else headerNavElem.after(contact);
            if (contact.contains(focused)) focused.focus({ preventScroll: true });
        };
        orderHeader();
        
        const openMobileMenu = () => {
            headerNavElem.inert = false;
            mobileMetaContainerElem.inert = false;
            headerNavElem.style.display = 'block';
            const reflow = headerNavElem.offsetHeight;
            headerNavElem.classList.add('header__nav--showing');

            mobileMetaContainerElem.style.display = 'block';
            const reflow2 = mobileMetaContainerElem.offsetHeight;
            mobileMetaContainerElem.classList.add('header__meta-container--mobile--showing');

            mobileControlsElem?.classList.add('header__mobile-controls--showing');
            mobileMenuIsOpen = true;
            mobileControlsElem.setAttribute('aria-expanded', 'true');
            mobileMenuElem.setAttribute('role', 'dialog');
            mobileMenuElem.setAttribute('aria-label', 'Navigation Menu');
            trap.activate();
            mobileControlsElem.focus();
        }

        const openSubMenu = (navItem) => {
            navItem.inert = false;
            navItem.style.display = 'block';
            const reflow = navItem.offsetHeight;
            navItem.classList.add('header__nav-item--second-level--showing');
        }

        const closeSubMenu = (navItem) => {
            navItem.inert = true;
            navItem?.classList.remove('header__nav-item--second-level--showing');
            afterAnimations(navItem, () => {
                if (!navItem.classList.contains('header__nav-item--second-level--showing')) {
                    navItem.removeAttribute('style');
                    navItem.inert = false;
                }
            });
        }

        const closeMobileMenu = () => {
            headerNavElem.inert = true;
            mobileMetaContainerElem.inert = true;
            (secondLevelElem as HTMLElement).style.display = 'none'
            headerNavElem?.classList.remove('header__nav--showing');
            afterAnimations(headerNavElem, () => {
                if (!mobileMenuIsOpen && window.innerWidth <= 1024) headerNavElem.style.display = 'none';
            }, false);

            mobileMetaContainerElem?.classList.remove('header__meta-container--mobile--showing');
            afterAnimations(mobileMetaContainerElem, () => {
                if (!mobileMenuIsOpen) mobileMetaContainerElem.style.display = 'none';
            }, false);
            
            mobileControlsElem?.classList.remove('header__mobile-controls--showing');
            mobileMenuIsOpen = false;
            mobileControlsElem.setAttribute('aria-expanded', 'false');
            trap.release();
            ['role', 'aria-label'].forEach(attribute => mobileMenuElem.removeAttribute(attribute));
            mobileControlsElem.focus();
        }

        mobileControlsElem?.addEventListener('click', () => {
            mobileMenuIsOpen ? closeMobileMenu() : openMobileMenu();
        })

        mobileMenuElem.addEventListener('keydown', event => {
            if (!mobileMenuIsOpen) return;
            if (event.key === 'Escape') closeMobileMenu();
        });
        
        const navItems = Array.from(headerNavElem.querySelectorAll('.header__nav-item')).filter((el) => el.querySelector(':scope > ul'));
        navItems.forEach(n => {
            const button = n.querySelector<HTMLButtonElement>(':scope > .header__nav-toggle');
            const childNavItem = n.querySelector('ul');
            button?.addEventListener('click', () => {
                if (!mobileViewport.matches) return;
                const expanded = button.getAttribute('aria-expanded') !== 'true';
                button.setAttribute('aria-expanded', String(expanded));
                if (expanded) openSubMenu(childNavItem);
                else closeSubMenu(childNavItem);
            });
        })

        return () => {
            orderHeader();
            if (mobileViewport.matches) return;
            const wasOpen = mobileMenuIsOpen;
            if (wasOpen) closeMobileMenu();
            headerNavElem.inert = false;
            if (wasOpen) headerElem?.querySelector<HTMLElement>('.header__nav-link')?.focus();
        };
    }

    const updateDesktop = isDesktop();
    const updateMobile = isMobile();

    mobileViewport.addEventListener('change', () => {
        updateDesktop?.();
        updateMobile?.();
        if (!mobileViewport.matches) {
            if (secondLevelElem) (secondLevelElem as HTMLElement).style.display = 'none';
            if (firstLevelNav) (firstLevelNav as HTMLElement).style.display = 'grid';
            const mobileMetaElem = headerElem?.querySelector('.header__meta-container--mobile--showing');
            if (mobileMetaElem)
                (mobileMetaElem as HTMLElement).style.display = 'none';
            const mobileControlsElem = headerElem?.querySelector('.header__mobile-controls.header__mobile-controls--showing');
            mobileControlsElem?.classList.remove('header__mobile-controls--showing');
            if (firstLevelNav) (firstLevelNav as HTMLElement).removeAttribute('style');
            headerElem?.querySelectorAll('.header__nav-item--second-level.header__nav-item--second-level--showing')
                .forEach((h) => {
                    h.removeAttribute('style');
                    h.classList.remove('header__nav-item--second-level--showing');
                })
        } else {
            if (secondLevelElem) (secondLevelElem as HTMLElement).style.display = 'none';
            if (firstLevelNav && !mobileMenuIsOpen) (firstLevelNav as HTMLElement).style.display = 'none';
        }
    })

    window.addEventListener('scroll', function() {
        if (window.scrollY > 0) {
            headerElem?.classList.add('header--scrolled');
        } else {
            headerElem?.classList.remove('header--scrolled');
        }
    });
};
